import {
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { AiCopilotService, CopilotStep } from '../ai-copilot.service';

type Mode = 'closed' | 'input' | 'thinking' | 'touring' | 'notfound' | 'done' | 'error';

// Screen name (from the backend registry) -> Angular route to auto-navigate to.
// Screens with no own route (e.g. modal-style Counselling) stay null.
const SCREEN_ROUTES: { [screen: string]: string | null } = {
  'OPD Visit': '/layout/OpdVisit',
  'Billing': '/layout/Billing',
  'Counselling': null,     // opened as a modal from EMR — cannot deep-link
  'Sidebar menu': null,    // the sidebar is always present
};

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-ai-copilot',
  templateUrl: './ai-copilot.component.html',
  styleUrls: ['./ai-copilot.component.scss'],
})
export class AiCopilotComponent implements OnDestroy {
  mode: Mode = 'closed';
  goal = '';
  intro = '';
  modelUsed = '';
  errorText = '';

  steps: CopilotStep[] = [];
  index = 0;

  // live geometry for spotlight + popover + arrow
  rect: Rect | null = null;
  popoverTop = 0;
  popoverLeft = 0;
  popoverBelow = true;

  confetti: { left: number; delay: number; bg: string; rot: number }[] = [];

  readonly examples = [
    'Register a new walk-in patient',
    'Find an existing patient by UHID',
    'Save the visit and generate the bill',
    'Clear the form and start over',
  ];

  private trackTimer: any = null;
  private pollTimer: any = null;
  private clickUnlisten: (() => void) | null = null;

  private navigatedFor = -1; // step index we've already auto-navigated for

  constructor(
    private copilot: AiCopilotService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnDestroy(): void {
    this.stopTracking();
    this.clearPoll();
    this.detachClick();
  }

  // ── Launcher / input ────────────────────────────────────────────────────────
  open(): void {
    this.mode = 'input';
    this.errorText = '';
    setTimeout(() => {
      const el = document.getElementById('aiCopilotInput');
      if (el) (el as HTMLInputElement).focus();
    }, 50);
  }

  close(): void {
    this.endTour();
    this.mode = 'closed';
  }

  useExample(ex: string): void {
    this.goal = ex;
    this.submit();
  }

  submit(): void {
    const goal = (this.goal || '').trim();
    if (!goal) return;
    this.mode = 'thinking';
    this.copilot.plan(goal).subscribe({
      next: (res) => {
        this.intro = res.intro;
        this.modelUsed = res.model;
        this.steps = res.steps || [];
        this.index = 0;
        if (!this.steps.length) {
          this.errorText =
            res.intro || 'I can\'t guide that one yet — try another task.';
          this.mode = 'error';
          return;
        }
        this.mode = 'touring';
        this.navigatedFor = -1;
        this.goToStep(0);
      },
      error: (err) => {
        this.errorText =
          'Could not reach the AI Copilot service. Is the AI backend running on :8000?';
        this.mode = 'error';
        // eslint-disable-next-line no-console
        console.error('Copilot plan error', err);
      },
    });
  }

  // ── Tour navigation ─────────────────────────────────────────────────────────
  get current(): CopilotStep | null {
    return this.steps[this.index] || null;
  }

  get progressPct(): number {
    if (!this.steps.length) return 0;
    return Math.round(((this.index + 1) / this.steps.length) * 100);
  }

  next(): void {
    if (this.index < this.steps.length - 1) {
      this.goToStep(this.index + 1);
    } else {
      this.finish();
    }
  }

  prev(): void {
    if (this.index > 0) this.goToStep(this.index - 1);
  }

  private goToStep(i: number): void {
    this.index = i;
    this.mode = 'touring';
    this.stopTracking();
    this.detachClick();
    this.clearPoll();
    this.locateWithRetry(0);
  }

  /**
   * Resolve an anchor id to a live element.
   * Sidebar links are tagged nav-<MenuName> (data-driven), so for "nav-*" ids we
   * fall back to a fuzzy match on the menu name instead of an exact id match.
   */
  private resolveAnchor(anchorId: string): HTMLElement | null {
    const exact = document.querySelector<HTMLElement>(
      `[data-ai-anchor="${anchorId}"]`,
    );
    if (exact) return exact;

    if (anchorId.startsWith('nav-')) {
      const key = anchorId.slice(4).replace(/[^a-z0-9]/gi, '').toLowerCase();
      const navs = Array.from(
        document.querySelectorAll<HTMLElement>('[data-ai-anchor^="nav-"]'),
      );
      for (const el of navs) {
        const raw = (el.getAttribute('data-ai-anchor') || '').slice(4);
        const norm = raw.replace(/[^a-z0-9]/gi, '').toLowerCase();
        if (norm && (norm.includes(key) || key.includes(norm))) return el;
      }
    }
    return null;
  }

  /** Poll for the anchor element (handles route changes / late rendering). */
  private locateWithRetry(attempt: number): void {
    const step = this.current;
    if (!step) return;
    const el = this.resolveAnchor(step.anchor);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      // let smooth-scroll settle, then lock on
      setTimeout(() => {
        this.measure(el);
        this.startTracking(el);
        this.attachClick(el);
        this.mode = 'touring';
        this.cdr.detectChanges();
      }, 350);
      return;
    }

    // Not on screen yet — try to AUTO-NAVIGATE to the screen this step lives on
    // (once per step), then keep polling for the element to render.
    const route = SCREEN_ROUTES[step.screen];
    if (route && this.navigatedFor !== this.index && !this.router.url.startsWith(route)) {
      this.navigatedFor = this.index;
      this.zone.run(() =>
        this.router.navigate([route], { queryParams: { clearHistory: true } }),
      );
    }

    if (attempt < 30) {
      this.pollTimer = setTimeout(() => this.locateWithRetry(attempt + 1), 300);
    } else {
      // give up gracefully — tell the user where to go (e.g. modal screens)
      this.mode = 'notfound';
      this.cdr.detectChanges();
    }
  }

  retryLocate(): void {
    this.clearPoll();
    this.locateWithRetry(0);
  }

  // ── Geometry tracking ───────────────────────────────────────────────────────
  private startTracking(el: HTMLElement): void {
    // keep the spotlight glued to the element while scrolling / layout shifts
    this.zone.runOutsideAngular(() => {
      this.trackTimer = setInterval(() => {
        const r = el.getBoundingClientRect();
        if (!this.rect ||
            r.top !== this.rect.top || r.left !== this.rect.left ||
            r.width !== this.rect.width || r.height !== this.rect.height) {
          this.zone.run(() => this.measure(el));
        }
      }, 120);
    });
  }

  private stopTracking(): void {
    if (this.trackTimer) {
      clearInterval(this.trackTimer);
      this.trackTimer = null;
    }
  }

  private clearPoll(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private measure(el: HTMLElement): void {
    const r = el.getBoundingClientRect();
    const pad = 6;
    this.rect = {
      top: r.top - pad,
      left: r.left - pad,
      width: r.width + pad * 2,
      height: r.height + pad * 2,
    };

    // popover placement: below if room, else above
    const popH = 168;
    const vh = window.innerHeight;
    this.popoverBelow = r.bottom + popH + 16 < vh;
    this.popoverTop = this.popoverBelow ? r.bottom + 18 : r.top - popH - 18;
    let left = r.left + r.width / 2 - 170;
    left = Math.max(12, Math.min(left, window.innerWidth - 352));
    this.popoverLeft = left;
  }

  // ── Click-to-advance ────────────────────────────────────────────────────────
  private attachClick(el: HTMLElement): void {
    const handler = () => {
      // small delay so the app reacts (e.g. navigates) before we move on
      setTimeout(() => this.zone.run(() => this.next()), 250);
    };
    el.addEventListener('click', handler, { once: true });
    this.clickUnlisten = () => el.removeEventListener('click', handler);
  }

  private detachClick(): void {
    if (this.clickUnlisten) {
      this.clickUnlisten();
      this.clickUnlisten = null;
    }
  }

  // ── Finish ──────────────────────────────────────────────────────────────────
  private finish(): void {
    this.endTour();
    this.mode = 'done';
    this.launchConfetti();
  }

  private endTour(): void {
    this.stopTracking();
    this.clearPoll();
    this.detachClick();
    this.rect = null;
  }

  restart(): void {
    this.confetti = [];
    this.mode = 'input';
    this.goal = '';
  }

  private launchConfetti(): void {
    const colors = ['#6366f1', '#22d3ee', '#a855f7', '#f59e0b', '#ec4899', '#10b981'];
    const pieces = [];
    for (let i = 0; i < 60; i++) {
      pieces.push({
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        bg: colors[i % colors.length],
        rot: Math.random() * 360,
      });
    }
    this.confetti = pieces;
    setTimeout(() => (this.confetti = []), 3500);
  }

  @HostListener('window:resize')
  onResize(): void {
    const step = this.current;
    if (this.mode === 'touring' && step) {
      const el = this.resolveAnchor(step.anchor);
      if (el) this.measure(el);
    }
  }
}
